import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import {
  Home, Receipt, AlertCircle, LogOut, Plus, Link2, Zap, FileText,
  Upload, CheckCircle2, Clock, XCircle, Download,
} from "lucide-react";
import { toast } from "sonner";
import { invoiceBadgeClass, invoiceStatusLabel, complaintBadgeClass } from "../lib/badges";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const DOC_TYPES = [
  { id: "aadhaar", label: "Aadhaar Card" },
  { id: "pan", label: "PAN / ID Proof" },
  { id: "photo", label: "Passport Photo" },
  { id: "college_id", label: "College / Office ID" },
];

const COMPLAINT_CATEGORIES = [
  "Plumbing", "Electricity", "AC", "TV", "Internet / Wi-Fi",
  "Furniture", "Cleaning", "Water Supply", "Door / Lock",
  "Pest Control", "Appliance", "Other",
];

function docStatusBadge(status) {
  if (status === "verified") return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2.5 py-0.5 text-xs uppercase tracking-wider font-semibold">
      <CheckCircle2 className="h-3 w-3" /> Verified
    </span>
  );
  if (status === "rejected") return (
    <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2.5 py-0.5 text-xs uppercase tracking-wider font-semibold">
      <XCircle className="h-3 w-3" /> Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 px-2.5 py-0.5 text-xs uppercase tracking-wider font-semibold">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
}

function docVerificationSub(docs) {
  if (docs.pending) return `${docs.pending} pending review`;
  if (docs.rejected) return `${docs.rejected} rejected — re-upload`;
  return "All documents verified";
}

function SumCard({ title, value, sub, badge }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
        {badge}
      </div>
      <div className="font-display text-3xl font-bold mt-2 truncate">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function AuthImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let blob;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => {
      blob = URL.createObjectURL(r.data);
      setUrl(blob);
    }).catch(() => setUrl(null));
    return () => { if (blob) URL.revokeObjectURL(blob); };
  }, [path]);
  if (!url) return <div className="h-32 bg-slate-100 flex items-center justify-center text-xs text-slate-400">Loading…</div>;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="meter" className="h-32 w-full object-cover border border-slate-200" /></a>;
}

function DashboardTab({ summary, today }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <SumCard title="Room" value={summary.room?.room_number || "—"}
               sub={`${summary.property?.name || ""} · ${summary.property?.city || ""}${summary.tenant.plot_number ? " · Plot " + summary.tenant.plot_number : ""}`} />
      <SumCard title="This month's rent"
               value={fmt(summary.rent.amount)}
               sub={summary.rent.due_date ? `Due ${summary.rent.due_date}` : "No invoice yet"}
               badge={summary.rent.status !== "none" && (
                 <span className={invoiceBadgeClass(summary.rent.status)}>
                   {invoiceStatusLabel({ status: summary.rent.status, payment_method: summary.rent.payment_method, due_date: summary.rent.due_date }, today)}
                 </span>
               )} />
      <SumCard title="Electricity bill"
               value={summary.electricity.has_reading ? fmt(summary.electricity.amount) : "—"}
               sub={summary.electricity.has_reading ? `Period ${summary.electricity.period}` : "No reading this month"} />
      <SumCard title="Open complaints" value={summary.open_complaints}
               sub={summary.open_complaints ? "Awaiting resolution" : "All clear"} />
      <SumCard title="Document verification"
               value={`${summary.documents.verified}/${summary.documents.total || DOC_TYPES.length}`}
               sub={docVerificationSub(summary.documents)} />
      <SumCard title="Security deposit" value={fmt(summary.tenant.deposit_amount)}
               sub={`Checked in ${summary.tenant.check_in_date}`} />
    </div>
  );
}

function RentTab({ invoices, today, onPay, onReceipt }) {
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      {invoices.length === 0 ? (
        <div className="p-12 text-center text-slate-500">No invoices yet.</div>
      ) : (
        <table className="w-full min-w-[640px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
              <th className="px-6 py-3">Period</th><th className="px-6 py-3">Amount</th>
              <th className="px-6 py-3">Due</th><th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Txn / Method</th><th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => {
              const overdue = i.status === "pending" && i.due_date < today;
              const status = overdue ? "overdue" : i.status;
              return (
                <tr key={i.id} className="border-b border-slate-100">
                  <td className="px-6 py-4 font-mono text-sm">{i.period}</td>
                  <td className="px-6 py-4 font-mono">{fmt(i.amount)}</td>
                  <td className="px-6 py-4 text-sm">{i.due_date}</td>
                  <td className="px-6 py-4"><span className={invoiceBadgeClass(status)} data-testid={`tenant-inv-status-${i.id}`}>{invoiceStatusLabel(i, today)}</span></td>
                  <td className="px-6 py-4 text-xs text-slate-500 font-mono">
                    {i.payment_method || "—"}
                  </td>
                  <td className="px-6 py-4 text-right space-x-1">
                    {i.status !== "paid" ? (
                      <button onClick={() => onPay(i.id, null)} data-testid={`tenant-pay-${i.id}`}
                              className="bg-blue-600 text-white px-4 py-1.5 text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
                        <Link2 className="h-3.5 w-3.5" /> Pay now
                      </button>
                    ) : (
                      <button onClick={() => onReceipt(i.id)}
                              data-testid={`receipt-${i.id}`}
                              className="bg-slate-100 hover:bg-slate-200 px-4 py-1.5 text-sm inline-flex items-center gap-1.5">
                        <Download className="h-3.5 w-3.5" /> Receipt
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ElectricityTab({ bills, onPay }) {
  return (
    <div className="space-y-4">
      {bills.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-500">
          No electricity bills posted yet.
        </div>
      ) : bills.map((b) => (
        <div key={b.id} className="bg-white border border-slate-200 p-6 grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Period {b.period}</div>
            <div className="font-display text-3xl font-bold mt-2">{fmt(b.amount)}</div>
            <div className="text-xs text-slate-500 mt-1">
              Rate ₹{b.rate_per_unit}/unit · Maintenance {fmt(b.maintenance_amount || 0)}
            </div>
            {b.paid ? (
              <span className="mt-3 inline-flex items-center gap-1 bg-green-100 text-green-800 px-2.5 py-0.5 text-xs uppercase tracking-wider font-semibold">
                <CheckCircle2 className="h-3 w-3" /> Paid {b.paid_on || ""}
              </span>
            ) : (
              <button onClick={() => onPay(null, b.id)} data-testid={`pay-elec-${b.id}`}
                      className="mt-3 bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" /> Pay now
              </button>
            )}
          </div>
          <div className="text-sm space-y-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Meter 1</div>
            <div className="font-mono">Prev: {b.previous_reading} → Curr: {b.current_reading}</div>
            <div className="font-mono text-slate-600">Units: {b.units}</div>
            {(b.previous_reading_2 || b.current_reading_2) ? (
              <>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-3 mb-2">Meter 2</div>
                <div className="font-mono">Prev: {b.previous_reading_2} → Curr: {b.current_reading_2}</div>
                <div className="font-mono text-slate-600">Units: {b.units_2 || 0}</div>
              </>
            ) : null}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Meter photo</div>
            {b.photo_path ? (
              <AuthImage path={b.photo_path} />
            ) : (
              <div className="h-32 bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                No photo uploaded
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ComplaintForm({ compForm, setCompForm, onSubmit, onCancel }) {
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 space-y-3" data-testid="tenant-complaint-form">
      <select required data-testid="tenant-comp-category"
              className="border border-slate-300 px-4 py-2.5 w-full bg-white"
              value={compForm.category}
              onChange={(e) => setCompForm({ ...compForm, category: e.target.value })}>
        <option value="">Select complaint area…</option>
        {COMPLAINT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input required placeholder="Title (e.g. Tap leakage)" data-testid="tenant-comp-title"
             className="border border-slate-300 px-4 py-2.5 w-full"
             value={compForm.title} onChange={(e) => setCompForm({ ...compForm, title: e.target.value })} />
      <textarea required placeholder="Describe the issue" rows={3} data-testid="tenant-comp-desc"
                className="border border-slate-300 px-4 py-2.5 w-full"
                value={compForm.description} onChange={(e) => setCompForm({ ...compForm, description: e.target.value })} />
      <select className="border border-slate-300 px-4 py-2.5"
              value={compForm.priority} onChange={(e) => setCompForm({ ...compForm, priority: e.target.value })}>
        {["low", "medium", "high"].map((p) => <option key={p} value={p}>Priority: {p}</option>)}
      </select>
      <div className="flex gap-3">
        <button data-testid="tenant-comp-save" className="bg-slate-900 text-white px-6 py-2.5">Submit</button>
        <button type="button" onClick={onCancel} className="bg-slate-100 px-6 py-2.5">Cancel</button>
      </div>
    </form>
  );
}

function ComplaintsTab({ complaints, compForm, setCompForm, showComp, setShowComp, onSubmit }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowComp(!showComp)} data-testid="tenant-add-complaint"
                className="bg-blue-600 text-white px-5 py-2.5 hover:bg-blue-700 inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Raise ticket
        </button>
      </div>
      {showComp && (
        <ComplaintForm compForm={compForm} setCompForm={setCompForm}
                       onSubmit={onSubmit} onCancel={() => setShowComp(false)} />
      )}
      <div className="bg-white border border-slate-200">
        {complaints.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No tickets raised yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {complaints.map((c) => (
              <li key={c.id} className="p-5">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      {c.category && (
                        <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
                          {c.category}
                        </span>
                      )}
                      <div className="font-medium">{c.title}</div>
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5">{c.description}</div>
                    {c.admin_remark && (
                      <div className="mt-2 text-xs bg-blue-50 border-l-2 border-blue-500 px-3 py-2 text-slate-700">
                        <b>Admin:</b> {c.admin_remark}
                      </div>
                    )}
                  </div>
                  <span className={complaintBadgeClass(c.status)}>{c.status.replace("_", " ")}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DocumentsTab({ documents, onDownloadAgreement, onUploadDocument }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="rental-agreement-card">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold">Your rental agreement</div>
          <h2 className="font-display text-2xl font-bold mt-1">Download rental agreement PDF</h2>
          <p className="text-sm text-slate-500 mt-1">
            Auto-generated with your name, photo, rent, deposit, room and property details.
          </p>
        </div>
        <button onClick={onDownloadAgreement} data-testid="download-agreement-btn"
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2 self-start">
          <Download className="h-4 w-4" /> Download PDF
        </button>
      </div>
      <div className="bg-white border border-slate-200 p-6">
        <h2 className="font-display text-2xl font-bold">Upload documents</h2>
        <p className="text-sm text-slate-500 mt-1">PDF/JPG/PNG, max 5 MB. Rejected documents can be re-uploaded.</p>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {DOC_TYPES.map((dt) => {
            const existing = documents.find((d) => d.doc_type === dt.id);
            return (
              <div key={dt.id} className="border border-slate-200 p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{dt.label}</div>
                  <div className="mt-1">{existing ? docStatusBadge(existing.status) : <span className="text-xs text-slate-400">Not uploaded</span>}</div>
                  {existing?.admin_remark && (
                    <div className="mt-2 text-xs text-red-700">Admin note: {existing.admin_remark}</div>
                  )}
                </div>
                <label className="bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800 cursor-pointer inline-flex items-center gap-1.5">
                  <Upload className="h-3.5 w-3.5" /> {existing ? "Re-upload" : "Upload"}
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg"
                         data-testid={`upload-${dt.id}`}
                         onChange={(e) => e.target.files[0] && onUploadDocument(dt.id, e.target.files[0])} />
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function TenantHome() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [compForm, setCompForm] = useState({ title: "", description: "", priority: "medium", category: "" });
  const [showComp, setShowComp] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, i, b, c, d] = await Promise.all([
        api.get("/tenant-portal/summary"),
        api.get("/tenant-portal/invoices"),
        api.get("/tenant-portal/electricity"),
        api.get("/tenant-portal/complaints"),
        api.get("/tenant-portal/documents"),
      ]);
      setSummary(s.data); setInvoices(i.data); setBills(b.data);
      setComplaints(c.data); setDocuments(d.data);
    } catch {
      navigate("/tenant");
    }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await api.post("/tenant-portal/logout");
    navigate("/tenant");
  };

  const payNow = async (invoiceId, meterId) => {
    try {
      const r = await api.post("/tenant-portal/payment-order", {
        invoice_id: invoiceId || null, meter_reading_id: meterId || null,
      });
      if (r.data.status === "mocked") {
        toast.message("Razorpay not configured — payment mocked", { description: r.data.note });
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Could not load Razorpay checkout"); return; }

      const rzp = new window.Razorpay({
        key: r.data.key_id,
        amount: Math.round(r.data.amount * 100),
        currency: r.data.currency,
        name: "StayCare",
        description: r.data.description,
        order_id: r.data.order_id,
        prefill: r.data.prefill,
        theme: { color: "#2563eb" },
        modal: {
          ondismiss: () => toast.info("Payment cancelled"),
        },
        handler: async (resp) => {
          try {
            await api.post("/tenant-portal/payment-verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              invoice_id: invoiceId || null,
              meter_reading_id: meterId || null,
            });
            toast.success("Payment successful");
            load();
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadReceipt = (invoiceId) => {
    window.open(`${API_BASE}/rent-invoices/${invoiceId}/receipt.pdf`, "_blank");
  };

  const submitComplaint = async (e) => {
    e.preventDefault();
    if (!compForm.category) { toast.error("Please choose a complaint area"); return; }
    try {
      await api.post("/tenant-portal/complaints", compForm);
      toast.success("Ticket raised");
      setCompForm({ title: "", description: "", priority: "medium", category: "" });
      setShowComp(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadDocument = async (docType, file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("File > 5MB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", "document");
      const up = await api.post("/tenant-portal/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/tenant-portal/documents", {
        doc_type: docType, storage_path: up.data.path, original_filename: file.name,
      });
      toast.success("Uploaded — pending admin verification");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadAgreement = async () => {
    try {
      const r = await api.get("/tenant-portal/agreement.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rental_agreement_${summary.tenant.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to download agreement"); }
  };

  if (!summary) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const today = new Date().toISOString().slice(0, 10);

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "rent", label: "Rent", icon: Receipt },
    { id: "electricity", label: "Electricity", icon: Zap },
    { id: "complaints", label: "Complaints", icon: AlertCircle },
    { id: "documents", label: "Documents", icon: FileText },
  ];

  return (
    <div className="min-h-dvh bg-slate-50" data-testid="tenant-home">
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <button onClick={() => setTab("dashboard")} data-testid="tenant-logo"
                  className="font-display text-xl sm:text-2xl font-black tracking-tighter whitespace-nowrap hover:opacity-80 transition-opacity">
            Stay<span className="text-blue-500">Care</span>
          </button>
          <button onClick={logout} data-testid="tenant-logout"
                  className="text-sm bg-slate-800 hover:bg-slate-700 px-3 sm:px-4 py-2 inline-flex items-center gap-2 min-h-[40px]">
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10 space-y-6 sm:space-y-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Welcome back</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1 break-words">{summary.tenant.name}</h1>
        </div>

        <div className="border-b border-slate-200 flex gap-4 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                    className={`px-1 py-3 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap min-h-[44px] ${
                      tab === id ? "border-blue-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardTab summary={summary} today={today} />}
        {tab === "rent" && <RentTab invoices={invoices} today={today} onPay={payNow} onReceipt={downloadReceipt} />}
        {tab === "electricity" && <ElectricityTab bills={bills} onPay={payNow} />}
        {tab === "complaints" && (
          <ComplaintsTab complaints={complaints} compForm={compForm} setCompForm={setCompForm}
                         showComp={showComp} setShowComp={setShowComp} onSubmit={submitComplaint} />
        )}
        {tab === "documents" && (
          <DocumentsTab documents={documents} onDownloadAgreement={downloadAgreement}
                        onUploadDocument={uploadDocument} />
        )}
      </main>
    </div>
  );
}
